import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Download, Search, Droplet, Image as ImageIcon, IdCard, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

export default function IdCards() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selected, setSelected] = useState({});
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("ready"); // ready | all | missing

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/id-cards/eligibility");
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load employees");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((it) => {
      if (filter === "ready" && !it.ready) return false;
      if (filter === "missing" && it.ready) return false;
      if (!q) return true;
      return (
        (it.name || "").toLowerCase().includes(q) ||
        (it.employee_id || "").toLowerCase().includes(q) ||
        (it.role || "").toLowerCase().includes(q) ||
        (it.email || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, filter]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const readyCount = items.filter((i) => i.ready).length;

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const setAll = (v, source) => {
    const next = { ...selected };
    source.forEach((c) => { if (c.ready) next[c.id] = v; });
    setSelected(next);
  };

  const generate = async () => {
    if (selectedIds.length === 0) { toast.error("Select at least one employee"); return; }
    setDownloading(true);
    try {
      const resp = await api.post("/id-cards/pdf", { user_ids: selectedIds }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `id_cards_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`PDF generated for ${selectedIds.length} employee${selectedIds.length === 1 ? "" : "s"}`);
    } catch (e) {
      logger.error("id card pdf", e);
      toast.error(e.response?.data?.detail || "Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="space-y-5 max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="label-overline">Team</span>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">ID Cards · Issue</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Generate a print-ready PDF (8 cards per A4 page) of selected employees — send straight to your card-printing vendor.
            Each card prints the passport photo, full name, employee ID, blood group and role.
          </p>
        </div>
        <Button
          onClick={generate}
          disabled={downloading || selectedIds.length === 0}
          className="bg-zinc-950 hover:bg-zinc-800 h-11"
          data-testid="idcards-download"
        >
          {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
          Download {selectedIds.length > 0 ? `(${selectedIds.length})` : ""} PDF
        </Button>
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="In scope" value={items.length} />
        <Stat label="Ready" value={readyCount} accent="green" />
        <Stat label="Missing details" value={items.length - readyCount} accent={items.length - readyCount > 0 ? "amber" : "neutral"} />
        <Stat label="Selected" value={selectedIds.length} accent="dark" />
      </div>

      {/* Filter strip */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, employee ID, role…"
            className="pl-9 h-10"
            data-testid="idcards-search"
          />
        </div>
        {[
          { value: "ready", label: "Ready", count: readyCount },
          { value: "missing", label: "Missing details", count: items.length - readyCount },
          { value: "all", label: "All", count: items.length },
        ].map((opt) => (
          <button
            key={opt.value}
            onClick={() => setFilter(opt.value)}
            className={`text-xs uppercase tracking-widest px-3 py-2 rounded-md border ${filter === opt.value ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}
            data-testid={`idcards-filter-${opt.value}`}
          >
            {opt.label} <span className="opacity-60 ml-1">({opt.count})</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-2 px-3 w-10">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.filter((c) => c.ready).every((c) => selected[c.id])}
                  onChange={(e) => setAll(e.target.checked, filtered)}
                  disabled={filtered.length === 0}
                  data-testid="idcards-select-all"
                />
              </th>
              <th className="py-2 px-3">Photo</th>
              <th className="py-2 px-3">Employee</th>
              <th className="py-2 px-3">Employee ID</th>
              <th className="py-2 px-3">Blood</th>
              <th className="py-2 px-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="py-12 text-center text-zinc-400">
                {filter === "ready" ? "No employees ready yet. Ask them to upload their passport photo and set their blood group from My Profile." : "No employees match."}
              </td></tr>
            )}
            {filtered.map((it) => {
              const disabled = !it.ready;
              return (
                <tr key={it.id} className={`border-b border-zinc-100 last:border-0 ${disabled ? "opacity-60" : ""}`} data-testid={`idcards-row-${it.id}`}>
                  <td className="py-2 px-3">
                    <input
                      type="checkbox"
                      checked={!!selected[it.id]}
                      disabled={disabled}
                      onChange={() => toggle(it.id)}
                      data-testid={`idcards-check-${it.id}`}
                    />
                  </td>
                  <td className="py-2 px-3">
                    {it.has_passport_photo ? (
                      <div className="w-9 h-11 rounded border border-emerald-200 bg-emerald-50 flex items-center justify-center" title="Passport photo uploaded">
                        <ImageIcon className="w-3.5 h-3.5 text-emerald-700" />
                      </div>
                    ) : (
                      <div className="w-9 h-11 rounded border border-zinc-200 bg-zinc-50 flex items-center justify-center" title="No passport photo">
                        <ImageIcon className="w-3.5 h-3.5 text-zinc-400" />
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <div className="font-medium text-zinc-900">{it.name}</div>
                    <div className="text-[10px] text-zinc-500">{(it.role || "").replace(/_/g, " ")}</div>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{it.employee_id || "—"}</td>
                  <td className="py-2 px-3">
                    {it.blood_group ? (
                      <span className="inline-flex items-center gap-1 text-xs font-bold text-red-700">
                        <Droplet className="w-3 h-3" /> {it.blood_group}
                      </span>
                    ) : <span className="text-amber-700 text-[11px] uppercase tracking-widest">Not set</span>}
                  </td>
                  <td className="py-2 px-3">
                    {it.ready ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                        <CheckCircle2 className="w-3 h-3" /> Ready
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        <AlertTriangle className="w-3 h-3" />
                        {it.has_passport_photo ? "Add blood group" : it.has_blood_group ? "Add photo" : "Photo + blood"}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
        <IdCard className="w-3.5 h-3.5" />
        Tip: employees fill their passport photo + blood group themselves from <span className="font-medium">My Profile → ID card details</span>. They cannot be checked here until both are set.
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const palette = {
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    dark: "bg-zinc-900 text-white border-zinc-900",
    neutral: "bg-white",
  };
  return (
    <div className={`border border-zinc-200 rounded-md p-3 ${palette[accent || "neutral"] || palette.neutral}`}>
      <div className={`text-[10px] uppercase tracking-widest ${accent === "dark" ? "text-zinc-400" : "text-zinc-500"}`}>{label}</div>
      <div className="text-2xl font-bold font-display mt-0.5">{value}</div>
    </div>
  );
}
